import { v4 as uuid } from "uuid";

import {
  SC_ToolPaletteMarkerId,
  SC_ToolPaletteMiscId,
  SC_ToolPalettePlayerId,
  SC_ToolPaletteStencilId,
  isSCBallId,
  isSCMiscId,
  isSCPlayerId,
} from "../../config";
import {
  ActionType,
  FrameStencil,
  MarkerBase,
  MarkerType,
  Player,
  Point,
  SoccerActionLineType,
  SoccerMiscType,
  SoccerPlayerLabelType,
  StencilType,
} from "../../types";

const getMarkerType = (id: SC_ToolPaletteMarkerId): MarkerType => {
  if (isSCPlayerId(id)) return MarkerType.PLAYER;
  else if (isSCBallId(id)) return MarkerType.BALL;
  else if (isSCMiscId(id)) return MarkerType.MISC;
  else if (id === "MISC_TEXTBOX") return MarkerType.TEXT;
  else if (id === "SHADING_RECTANGLE") return MarkerType.SHADING_RECTANGLE;
  else if (id === "SHADING_OVAL") return MarkerType.SHADING_OVAL;
  else if (id === "SHADING_TRAPEZOID") return MarkerType.SHADING_TRAPEZOID;
  else throw new Error(`Invalid SC_ToolPaletteMarkerId: ${id}`);
};

const getPlayerIdNumber = (id: SC_ToolPalettePlayerId): Player["playerIdNumber"] => {
  switch (id) {
    case "SC_PLAYER_1":
    case "SC_PLAYER_POSSESSION_1":
    case "SC_PLAYER_FILL_1":
      return 1;

    case "SC_PLAYER_2":
    case "SC_PLAYER_POSSESSION_2":
    case "SC_PLAYER_FILL_2":
      return 2;

    case "SC_PLAYER_3":
    case "SC_PLAYER_POSSESSION_3":
    case "SC_PLAYER_FILL_3":
      return 3;

    case "SC_PLAYER_4":
    case "SC_PLAYER_POSSESSION_4":
    case "SC_PLAYER_FILL_4":
      return 4;

    case "SC_PLAYER_5":
    case "SC_PLAYER_POSSESSION_5":
    case "SC_PLAYER_FILL_5":
      return 5;

    case "SC_PLAYER_6":
    case "SC_PLAYER_POSSESSION_6":
    case "SC_PLAYER_FILL_6":
      return 6;

    case "SC_PLAYER_7":
    case "SC_PLAYER_POSSESSION_7":
    case "SC_PLAYER_FILL_7":
      return 7;

    case "SC_PLAYER_8":
    case "SC_PLAYER_POSSESSION_8":
    case "SC_PLAYER_FILL_8":
      return 8;

    case "SC_PLAYER_9":
    case "SC_PLAYER_POSSESSION_9":
    case "SC_PLAYER_FILL_9":
      return 9;

    case "SC_PLAYER_10":
    case "SC_PLAYER_POSSESSION_10":
    case "SC_PLAYER_FILL_10":
      return 10;

    case "SC_PLAYER_11":
    case "SC_PLAYER_POSSESSION_11":
    case "SC_PLAYER_FILL_11":
      return 11;
  }
};

const getPlayerLabel = (id: SC_ToolPalettePlayerId, labelType: SoccerPlayerLabelType): string => {
  const isO = labelType === "o";
  const isNum = labelType === "NUM";

  switch (id) {
    case "SC_PLAYER_1":
    case "SC_PLAYER_POSSESSION_1":
    case "SC_PLAYER_FILL_1":
      return isO ? "o" : isNum ? "1" : "D";

    case "SC_PLAYER_2":
    case "SC_PLAYER_POSSESSION_2":
    case "SC_PLAYER_FILL_2":
      return isNum ? "2" : "D";

    case "SC_PLAYER_3":
    case "SC_PLAYER_POSSESSION_3":
    case "SC_PLAYER_FILL_3":
      return isNum ? "3" : "D";

    case "SC_PLAYER_4":
    case "SC_PLAYER_POSSESSION_4":
    case "SC_PLAYER_FILL_4":
      return isNum ? "4" : "D";

    case "SC_PLAYER_5":
    case "SC_PLAYER_POSSESSION_5":
    case "SC_PLAYER_FILL_5":
      return isNum ? "5" : "M";

    case "SC_PLAYER_6":
    case "SC_PLAYER_POSSESSION_6":
    case "SC_PLAYER_FILL_6":
      return isNum ? "6" : "M";

    case "SC_PLAYER_7":
    case "SC_PLAYER_POSSESSION_7":
    case "SC_PLAYER_FILL_7":
      return isNum ? "7" : "M";

    case "SC_PLAYER_8":
    case "SC_PLAYER_POSSESSION_8":
    case "SC_PLAYER_FILL_8":
      return isNum ? "8" : "M";

    case "SC_PLAYER_9":
    case "SC_PLAYER_POSSESSION_9":
    case "SC_PLAYER_FILL_9":
      return isNum ? "9" : "F";

    case "SC_PLAYER_10":
    case "SC_PLAYER_POSSESSION_10":
    case "SC_PLAYER_FILL_10":
      return isNum ? "10" : "F";

    case "SC_PLAYER_11":
    case "SC_PLAYER_POSSESSION_11":
    case "SC_PLAYER_FILL_11":
      return isNum ? "11" : "GK";
  }
};

const getPlayerFill = (id: SC_ToolPalettePlayerId): boolean => {
  switch (id) {
    case "SC_PLAYER_FILL_1":
    case "SC_PLAYER_FILL_2":
    case "SC_PLAYER_FILL_3":
    case "SC_PLAYER_FILL_4":
    case "SC_PLAYER_FILL_5":
    case "SC_PLAYER_FILL_6":
    case "SC_PLAYER_FILL_7":
    case "SC_PLAYER_FILL_8":
    case "SC_PLAYER_FILL_9":
    case "SC_PLAYER_FILL_10":
    case "SC_PLAYER_FILL_11":
      return true;

    case "SC_PLAYER_1":
    case "SC_PLAYER_2":
    case "SC_PLAYER_3":
    case "SC_PLAYER_4":
    case "SC_PLAYER_5":
    case "SC_PLAYER_6":
    case "SC_PLAYER_7":
    case "SC_PLAYER_8":
    case "SC_PLAYER_9":
    case "SC_PLAYER_10":
    case "SC_PLAYER_11":
    case "SC_PLAYER_POSSESSION_1":
    case "SC_PLAYER_POSSESSION_2":
    case "SC_PLAYER_POSSESSION_3":
    case "SC_PLAYER_POSSESSION_4":
    case "SC_PLAYER_POSSESSION_5":
    case "SC_PLAYER_POSSESSION_6":
    case "SC_PLAYER_POSSESSION_7":
    case "SC_PLAYER_POSSESSION_8":
    case "SC_PLAYER_POSSESSION_9":
    case "SC_PLAYER_POSSESSION_10":
    case "SC_PLAYER_POSSESSION_11":
      return false;
  }
};

const playerHasPossession = (id: SC_ToolPalettePlayerId): boolean => {
  switch (id) {
    case "SC_PLAYER_POSSESSION_1":
    case "SC_PLAYER_POSSESSION_2":
    case "SC_PLAYER_POSSESSION_3":
    case "SC_PLAYER_POSSESSION_4":
    case "SC_PLAYER_POSSESSION_5":
    case "SC_PLAYER_POSSESSION_6":
    case "SC_PLAYER_POSSESSION_7":
    case "SC_PLAYER_POSSESSION_8":
    case "SC_PLAYER_POSSESSION_9":
    case "SC_PLAYER_POSSESSION_10":
    case "SC_PLAYER_POSSESSION_11":
      return true;
    case "SC_PLAYER_1":
    case "SC_PLAYER_2":
    case "SC_PLAYER_3":
    case "SC_PLAYER_4":
    case "SC_PLAYER_5":
    case "SC_PLAYER_6":
    case "SC_PLAYER_7":
    case "SC_PLAYER_8":
    case "SC_PLAYER_9":
    case "SC_PLAYER_10":
    case "SC_PLAYER_11":
    case "SC_PLAYER_FILL_1":
    case "SC_PLAYER_FILL_2":
    case "SC_PLAYER_FILL_3":
    case "SC_PLAYER_FILL_4":
    case "SC_PLAYER_FILL_5":
    case "SC_PLAYER_FILL_6":
    case "SC_PLAYER_FILL_7":
    case "SC_PLAYER_FILL_8":
    case "SC_PLAYER_FILL_9":
    case "SC_PLAYER_FILL_10":
    case "SC_PLAYER_FILL_11":
      return false;
  }
};

const getMiscType = (id: SC_ToolPaletteMiscId): SoccerMiscType => {
  switch (id) {
    case "MISC_GOAL":
      return SoccerMiscType.GOAL;
    case "MISC_BALL":
      return SoccerMiscType.BALL_STATIC;
    case "MISC_CONE":
      return SoccerMiscType.CONE;
    case "MISC_FLAG":
      return SoccerMiscType.FLAG;
    case "MISC_LADDER":
      return SoccerMiscType.LADDER;
  }
};

export interface CreateSoccerStencilFromToolPaletteProps {
  id: SC_ToolPaletteStencilId;
  positionLabelType: SoccerPlayerLabelType;
  pos: Point;
}
export const createSoccerStencilFromToolPalette = ({
  id,
  positionLabelType,
  pos,
}: CreateSoccerStencilFromToolPaletteProps): FrameStencil | null => {
  const stencilId = uuid();
  if (id === "DHO")
    return {
      id: stencilId,
      stencilType: StencilType.ACTION,
      actionType: ActionType.HANDOFF,
      layerType: SoccerActionLineType.SC_HANDOFF,
      pos,
      midPoints: [],
      startPos: pos,
      endPos: pos,
      startTime: 0,
      endTime: 0,
      prevPlayer: null,
      nextPlayer: null,
      prevAction: null,
      nextAction: null,
    };

  const markerType = getMarkerType(id);
  const markerBase: MarkerBase = { id: stencilId, pos, stencilType: StencilType.MARKER };

  switch (markerType) {
    case MarkerType.PLAYER: {
      if (!isSCPlayerId(id)) return null;
      return {
        ...markerBase,
        markerType,
        playerIdNumber: getPlayerIdNumber(id),
        label: getPlayerLabel(id, positionLabelType),
        fill: getPlayerFill(id),
        possession: playerHasPossession(id),
        color: "#000",
        movements: [],
      };
    }

    case MarkerType.BALL:
      return { ...markerBase, markerType, associatedPlayer: "", movements: [] };

    case MarkerType.MISC: {
      if (!isSCMiscId(id)) return null;
      return { ...markerBase, markerType, miscType: getMiscType(id) };
    }

    case MarkerType.SHADING_RECTANGLE:
      return { ...markerBase, markerType, width: 5, height: 10 };

    case MarkerType.SHADING_OVAL:
      return { ...markerBase, markerType, rx: 2.5, ry: 5 };

    case MarkerType.SHADING_TRAPEZOID: {
      const { x, y } = pos;
      return {
        ...markerBase,
        markerType,
        shadeVertices: [
          { x, y },
          { x: x + 10, y },
          { x: x + 7.5, y: y + 10 },
          { x: x + 2.5, y: y + 10 },
        ],
      };
    }

    default:
      return null;
  }
};
